import openai from './openaiClient';

/**
 * Generates a streaming chat completion response for the BeeWise AI assistant.
 * @param {string} userMessage - The user's input message.
 * @param {Array} conversationHistory - Previous messages in the conversation.
 * @param {Function} onChunk - Callback to handle each streamed chunk.
 * @param {Function} onComplete - Callback when streaming is complete.
 */
export async function getBeeWiseAssistantResponse(
  userMessage, 
  conversationHistory = [], 
  onChunk, 
  onComplete
) {
  try {
    const systemMessage = {
      role: 'system',
      content: `You are "Bee", the AI assistant for BeeWise - a B2B SaaS platform. Your role is to help employees with:
- Company information and policies
- Task management and productivity tips
- Navigation guidance through the platform
- Answering common workplace questions (WiFi password, time-off requests, etc.)
- Providing helpful resources and tool recommendations

Be friendly, professional, and concise. Use a warm, supportive tone that matches BeeWise's collaborative culture.`
    };

    const messages = [
      systemMessage,
      ...conversationHistory,
      { role: 'user', content: userMessage }
    ];

    const stream = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini', // Updated to use correct model name
      messages,
      stream: true,
      max_tokens: 500 // Updated parameter name for standard OpenAI API
    });

    let fullResponse = '';

    for await (const chunk of stream) {
      const content = chunk?.choices?.[0]?.delta?.content || '';
      if (content) {
        fullResponse += content;
        onChunk(content);
      }
    }

    if (onComplete) {
      onComplete(fullResponse);
    }

    return fullResponse;
  } catch (error) {
    console.error('Error in BeeWise assistant response:', error);
    
    // Enhanced error handling with specific error types
    const enhancedError = new Error();
    enhancedError.originalError = error;
    
    if (error?.status === 429 || error?.message?.includes('quota') || error?.message?.includes('exceeded')) {
      enhancedError.type = 'QUOTA_EXCEEDED';
      enhancedError.message = 'OpenAI API quota exceeded. Please check your billing and plan details at platform.openai.com/account/billing';
      enhancedError.userMessage = '⚠️ API quota exceeded. Please verify your OpenAI API key has sufficient credits and an active billing plan.';
    } else if (error?.status === 401 || error?.message?.includes('authentication') || error?.message?.includes('API key')) {
      enhancedError.type = 'AUTHENTICATION_ERROR';
      enhancedError.message = 'Invalid or missing OpenAI API key';
      enhancedError.userMessage = '🔐 Authentication failed. Please verify your OpenAI API key is correct and active.';
    } else if (error?.status === 503 || error?.message?.includes('service unavailable')) {
      enhancedError.type = 'SERVICE_UNAVAILABLE';
      enhancedError.message = 'OpenAI service temporarily unavailable';
      enhancedError.userMessage = '⏳ OpenAI service is temporarily unavailable. Please try again in a few moments.';
    } else {
      enhancedError.type = 'UNKNOWN_ERROR';
      enhancedError.message = error?.message || 'Unknown error occurred';
      enhancedError.userMessage = '❌ An unexpected error occurred. Please try again or contact support if the issue persists.';
    }
    
    throw enhancedError;
  }
}

/**
 * Generates context-aware quick responses for common queries.
 * @param {string} queryType - Type of quick query ('company_info', 'help', 'next_steps', 'hr').
 * @returns {Promise<string>} Quick response text.
 */
export async function getQuickResponse(queryType) {
  const queryMap = {
    'company_info': 'Tell me about BeeWise company culture and values.',
    'help': 'What tasks should I focus on today?',
    'next_steps': 'What are my next steps in the onboarding process?',
    'hr': 'How do I contact HR or request time off?'
  };

  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-4o-mini', // Updated to use correct model name
      messages: [
        {
          role: 'system',
          content: 'You are "Bee", the BeeWise AI assistant. Provide concise, helpful responses to employee questions.'
        },
        {
          role: 'user',
          content: queryMap?.[queryType] || 'How can you help me?'
        }
      ],
      max_tokens: 200 // Updated parameter name
    });

    return response?.choices?.[0]?.message?.content;
  } catch (error) {
    console.error('Error in quick response:', error);
    
    // Enhanced error handling for quick responses
    const enhancedError = new Error();
    enhancedError.originalError = error;
    
    if (error?.status === 429 || error?.message?.includes('quota') || error?.message?.includes('exceeded')) {
      enhancedError.type = 'QUOTA_EXCEEDED';
      enhancedError.userMessage = '⚠️ API quota exceeded. Please verify your OpenAI API key has sufficient credits.';
    } else if (error?.status === 401) {
      enhancedError.type = 'AUTHENTICATION_ERROR';
      enhancedError.userMessage = '🔐 Authentication failed. Please verify your OpenAI API key.';
    } else {
      enhancedError.type = 'UNKNOWN_ERROR';
      enhancedError.userMessage = '❌ Unable to get quick response. Please try a manual message instead.';
    }
    
    throw enhancedError;
  }
}